import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BALLOONS } from "../../lib/media";

const CONFETTI_COLORS = ["#FFD700", "#E6C200", "#C0C0C0", "#E8E8E8", "#FFFFFF"];

function makeHearts(count) {
  const arr = [];
  for (let i = 0; i < count; i++) {
    arr.push({
      id: `h-${i}`,
      src: Math.random() < 0.5 ? BALLOONS.heartGold : BALLOONS.heartSilver,
      left: Math.random() * 100, // %
      startDelay: Math.random() * 1.8,
      size: 70 + Math.random() * 100,
      drift: (Math.random() - 0.5) * 220,
      rot: (Math.random() - 0.5) * 36,
      dur: 5 + Math.random() * 3,
    });
  }
  return arr;
}

function makeConfetti(count) {
  const arr = [];
  for (let i = 0; i < count; i++) {
    arr.push({
      id: `c-${i}`,
      left: Math.random() * 100,
      startDelay: Math.random() * 2.4,
      width: 6 + Math.random() * 10,
      height: 3 + Math.random() * 6,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      drift: (Math.random() - 0.5) * 320,
      rot: (Math.random() - 0.5) * 720,
      dur: 4.5 + Math.random() * 3.5,
    });
  }
  return arr;
}

export default function BalloonDrop({ onDone, duration = 10000 }) {
  const [vh, setVh] = useState(
    typeof window !== "undefined" ? window.innerHeight : 800
  );
  const hearts = useMemo(() => makeHearts(22), []);
  const confetti = useMemo(() => makeConfetti(110), []);

  useEffect(() => {
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => onDone?.(), duration);
    return () => clearTimeout(t);
  }, [duration, onDone]);

  return (
    <div
      data-testid="balloon-drop"
      className="fixed inset-0 z-[95] pointer-events-none overflow-hidden"
    >
      {/* Hearts — gold + silver only */}
      {hearts.map((h) => (
        <motion.div
          key={h.id}
          initial={{ x: 0, y: -260, rotate: 0, opacity: 0 }}
          animate={{
            x: h.drift,
            y: vh + 260,
            rotate: h.rot,
            opacity: [0, 1, 1, 1, 0.95],
          }}
          transition={{
            duration: h.dur,
            delay: h.startDelay,
            ease: [0.22, 0.8, 0.35, 1],
            opacity: { duration: h.dur, delay: h.startDelay, times: [0, 0.05, 0.5, 0.9, 1] },
          }}
          style={{
            position: "absolute",
            top: 0,
            left: `${h.left}%`,
            width: h.size,
            height: h.size * 1.4,
          }}
        >
          <motion.div
            animate={{ x: [0, 12, -10, 8, 0] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
            className="w-full h-full"
          >
            <img
              src={h.src}
              alt=""
              className="w-full h-full object-contain balloon select-none"
              draggable={false}
            />
          </motion.div>
        </motion.div>
      ))}

      {/* Confetti — gold + silver + white only */}
      {confetti.map((c) => (
        <motion.div
          key={c.id}
          initial={{ x: 0, y: -40, rotate: 0, opacity: 0 }}
          animate={{
            x: c.drift,
            y: vh + 80,
            rotate: c.rot,
            opacity: [0, 1, 1, 1, 0],
          }}
          transition={{
            duration: c.dur,
            delay: c.startDelay,
            ease: [0.3, 0.8, 0.6, 1],
          }}
          style={{
            position: "absolute",
            top: 0,
            left: `${c.left}%`,
            width: c.width,
            height: c.height,
            background: c.color,
            borderRadius: 1,
            boxShadow:
              c.color === "#FFD700" || c.color === "#E6C200"
                ? "0 0 4px rgba(255, 215, 0, 0.45)"
                : c.color === "#C0C0C0" || c.color === "#E8E8E8"
                  ? "0 0 4px rgba(220, 220, 220, 0.45)"
                  : "none",
          }}
        />
      ))}

      {/* 4-corner confetti bursts — gold + silver + white shooting outward */}
      <CornerBurst origin="top-left" />
      <CornerBurst origin="top-right" />
      <CornerBurst origin="bottom-left" />
      <CornerBurst origin="bottom-right" />
    </div>
  );
}

function CornerBurst({ origin }) {
  const pieces = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 28; i++) {
      const angle = Math.random() * (Math.PI / 2); // quadrant fan
      const distance = 220 + Math.random() * 480;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;
      arr.push({
        id: `${origin}-${i}`,
        x,
        y,
        rot: (Math.random() - 0.5) * 720,
        dur: 1.8 + Math.random() * 1.0,
        delay: Math.random() * 0.18,
        size: 7 + Math.random() * 9,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      });
    }
    return arr;
  }, [origin]);

  // Each origin maps to a corner anchor + sign for x/y direction
  const anchor = {
    "top-left":     { top: 0,    left: 0,    sx:  1, sy:  1 },
    "top-right":    { top: 0,    right: 0,   sx: -1, sy:  1 },
    "bottom-left":  { bottom: 0, left: 0,    sx:  1, sy: -1 },
    "bottom-right": { bottom: 0, right: 0,   sx: -1, sy: -1 },
  }[origin];

  return (
    <div
      className="absolute pointer-events-none"
      style={{ ...anchor, width: 1, height: 1 }}
      data-testid={`confetti-corner-${origin}`}
    >
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          initial={{ x: 0, y: 0, rotate: 0, opacity: 0, scale: 0.4 }}
          animate={{
            x: p.x * anchor.sx,
            y: p.y * anchor.sy,
            rotate: p.rot,
            opacity: [0, 1, 1, 0.9, 0],
            scale: [0.4, 1, 1, 1, 0.7],
          }}
          transition={{
            duration: p.dur,
            delay: p.delay,
            ease: [0.18, 0.9, 0.42, 1.0],
            times: [0, 0.08, 0.55, 0.85, 1],
          }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: p.size,
            height: p.size * 0.4,
            background: p.color,
            borderRadius: 1,
            boxShadow:
              p.color === "#FFD700" || p.color === "#E6C200"
                ? "0 0 5px rgba(255, 215, 0, 0.5)"
                : p.color === "#C0C0C0" || p.color === "#E8E8E8"
                  ? "0 0 5px rgba(220, 220, 220, 0.5)"
                  : "none",
          }}
        />
      ))}
    </div>
  );
}
